import type { ButtonProps, SelectProps } from "@aurem/ui";
import { Button, Select } from "@aurem/ui";
import { GlobeIcon } from "lucide-react";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../../providers/I18nProvider";

type Props = {
  buttonProps?: ButtonProps;
  selectProps?: Omit<
    SelectProps,
    "options" | "value" | "onChange" | "multiple"
  >;
};

export const LanguageSelect = ({ buttonProps, selectProps }: Props) => {
  const { language, languages, languageKeys, setLanguage } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const options = languageKeys.map((code) => ({
    id: code,
    text: languages[code],
  }));

  const handleChange = useCallback(
    (value: string | undefined) => {
      if (!value || value === language) return;

      setLanguage(value as typeof language);

      const pathname = location.pathname.replace(/^\/[^/]+/, `/${value}`);
      void navigate(`${pathname}${location.search}`);
    },
    [language, setLanguage, location, navigate],
  );

  const renderAnchor = useCallback(() => {
    return (
      <Button
        variant="outlined"
        color="primary"
        size="sm"
        startIcon={<GlobeIcon />}
        {...buttonProps}
      >
        {buttonProps?.children ?? <>{language.toUpperCase()}</>}
      </Button>
    );
  }, [buttonProps, language]);

  return (
    <Select
      options={options}
      value={language}
      onChange={handleChange}
      onRenderAnchor={renderAnchor}
      matchTriggerWidth={false}
      placement="bottom-end"
      minWidth={120}
      {...selectProps}
    />
  );
};
